import { Component, Input, OnChanges } from '@angular/core';
import { lightAppearAnimations } from '../../../../util/animations/light-appear.animations';
import { fixedAppearAnimations } from '../../../../util/animations/fixed-appear-animations';
import { appearAnimations } from '../../../../util/animations/appear-animations';
import { ToolTipSettings } from '../tooltip/tooltip.util';

@Component({
  selector: 'app-vertical-charts',
  templateUrl: './vertical-charts.component.html',
  styleUrls: ['./vertical-charts.component.scss'],
  animations: [lightAppearAnimations, fixedAppearAnimations, appearAnimations],
})
export class VerticalChartsComponent implements OnChanges {
  @Input() data: VerticalChartObject;
  // precondition (>= 2) because there is a division on (yLevels-1) in html.
  @Input() yLevels = 5;

  parsedGraphicsData: Graphic;
  disabledColors: string[] = [];
  WIDTH_PER_GRAPHIC = WIDTH_PER_GRAPHIC;
  COUNT_PER_GRAPHIC = COUNT_PER_GRAPHIC;

  toolTipSettings: VerticalChartPopUpSettings = {
    show: false,
  };

  ngOnChanges(): void {
    if (this.data) {
      this.calculateGraphics();
    } else {
      this.resetData();
    }
  }

  activateTooltip(id: string, graphic: VerticalChartGraphic): void {
    if (this.areAllPartsEmpty(graphic)) {
      return;
    }
    this.toolTipSettings = {
      show: true,
      coordinates: document.getElementById(id).getBoundingClientRect(),
      graphic,
    };
  }


  // User clicks to show/hide bar (parts in the bar) with specific color.
  toggleColor(color: string): void {
    const index = this.disabledColors.indexOf(color);
    if (index > -1) {
      this.disabledColors.splice(index);
      this.data.agenda.find((a) => a.color === color).blurred = false;
    } else {
      this.disabledColors.push(color);
      this.data.agenda.find((a) => a.color === color).blurred = true;
    }
    this.calculateGraphics();
  }

  private calculateGraphics(): void {
    // Deep copy of data to not change the original data.
    let graphics: VerticalChartGraphic[] = JSON.parse(JSON.stringify(this.data.graphics));

    let minAmount = 0;
    let maxAmount = 0;

    graphics.forEach((g) => g.bars.forEach((b) => {
      maxAmount = Math.max(maxAmount,
        b.parts.filter((p) => p.amount > 0)
          .reduce((p1, p2) => p1 + p2.amount, 0));

      minAmount = Math.min(minAmount,
        b.parts.filter((p) => p.amount < 0)
          .reduce((p1, p2) => p1 + p2.amount, 0));
    }));
    const yAxis = this.getYAxisSpan(minAmount, maxAmount, this.yLevels);
    const yAxisLengthWithoutZero = yAxis.length - 1;
    const yZero = ((yAxisLengthWithoutZero - yAxis.indexOf(0)) / yAxisLengthWithoutZero) * 100;
    const yDiff = yAxis[0] - yAxis[yAxis.length - 1];

    graphics = this.removePartsWithDisabledColors(graphics);

    for (let i = 0; i < this.data.graphics.length; i++) {
      for (let j = 0; j < this.data.graphics[i].bars.length; j++) {
        // Calculate the last positive (top) / negative (bottom) parts of the bars to have a proper css of ending bar.
        const positiveAmount = graphics[i].bars[j].parts.filter((p) => p.amount > 0);
        const negativeAmount = graphics[i].bars[j].parts.filter((p) => p.amount < 0);

        if (!negativeAmount.length) {
          graphics[i].bars[j].bottom = yZero;
        } else {
          graphics[i].bars[j].bottom = yZero + (negativeAmount
            .reduce((a, b) => a + b.amount, 0) / yDiff * 100);
        }

        negativeAmount.map((a) => {
          a.pcntAmount = Math.abs(a.amount / yDiff);
          a.isPositive = false;
        });
        positiveAmount.map((a) => {
          a.pcntAmount = a.amount / yDiff;
          a.isPositive = true;
        });
        graphics[i].bars[j].parts = positiveAmount.concat(negativeAmount);
      }
      // Sets the 0-level of the y. Is needed e.x. to set the titles of the graphics.
      graphics[i].yAxisZero = yZero;
    }
    this.parsedGraphicsData = {
      yAxis,
      yZero,
      graphics,
    };
  }

  /**
   * Checks if all bars in the given VerticalChartGraphic have empty parts arrays == all data set is empty
   *
   * @param graphic - The fund data to be checked
   * @returns True if every bar's parts array is empty, false otherwise.
   */
  private areAllPartsEmpty(graphic: VerticalChartGraphic): boolean {
    return graphic.bars.every((bar) => bar.parts.length === 0);
  }

  /**
   * Provides a blyatifull array of n* equally distributed values.
   * E.x:
   * (-373, 2225) => [-600, 0, 600, 1200, 1800, 2400]
   * (10, 2) => [0,5,10]
   *
   * @param min is the bottom limit.
   * @param max is the upper limit.
   * @param stepAmount is the n*. Tells, how many numbers should this function return.
   * Because it returns blyatifull array, it may provide +-1 steps.
   */
  private getYAxisSpan(min: number, max: number, stepAmount: number): number[] {
    const arr = [0];

    const diff = max - min;
    const step = diff * STEP_MULTIPLE / (stepAmount);
    const magnifier = 5 * Math.pow(10, this.countNumberLength(step) - 1);
    const roundedUpStep = Math.ceil(step / magnifier) * magnifier;

    let counter = 0;
    while (counter > min) {
      counter -= roundedUpStep;
      arr.push(counter);
    }
    counter = 0;
    while (counter < max) {
      counter += roundedUpStep;
      arr.push(counter);
    }
    arr.sort((a, b) => b - a);
    return arr;
  }

  /**
   * Removes all colors, that are in the disabledColors.
   *
   * @see disabledColors.
   * @param graphics to be removed from.
   */
  private removePartsWithDisabledColors(graphics: VerticalChartGraphic[]): VerticalChartGraphic[] {
    graphics.forEach((g) => g.bars.forEach((b) => b.parts.map((p) => {
      p.amount = this.disabledColors.includes(p.color) ? 0 : p.amount;
    })));
    return graphics;
  }

  /**
   * Provides the exponent of 10 of normalized number.
   * E.x. 10 -> 1, 150 -> 2, 1000 -> 3.
   *
   * @param num is input.
   * @return num.length.
   */
  private countNumberLength(num: number): number {
    num = Math.abs(num);
    let numLength = 0;
    while (num >= 10) {
      num /= 10;
      numLength++;
    }
    return numLength;
  }

  private resetData(): void {
    this.parsedGraphicsData = undefined;
    this.disabledColors = [];
  }
}

export interface VerticalChartObject {
  agenda: VerticalChartAgenda[];
  graphics: VerticalChartGraphic[];
}

export interface VerticalChartAgenda {
  color: string;
  title: string;
  blurred?: boolean;
}

export interface VerticalChartGraphic {
  bars: VerticalChartBar[];
  title: string;
  yAxisZero?: number;
}

export interface VerticalChartBar {
  parts: VerticalChartBarPart[];
  bottom?: number;
}

export interface VerticalChartBarPart {
  color: string;
  legendHeading?: string;
  amount: number;
  pcntAmount?: number;
  isPositive?: boolean;
}

interface Graphic {
  yAxis: number[];
  yZero: number;
  graphics: VerticalChartGraphic[];
}

interface VerticalChartPopUpSettings extends ToolTipSettings {
  graphic?: VerticalChartGraphic;
}

// Says, how much a graphic bounds should be adjusted.
const STEP_MULTIPLE = 1.1;
// From which graphic count a min-width should be applied. Is needed to extend the horizontal lines to the right-end of the graphic.
const COUNT_PER_GRAPHIC = 7;
// The width in percents to multiply for each graphic. Is needed to extend the horizontal lines to the right-end of the graphic.
const WIDTH_PER_GRAPHIC = 20;
